import React from 'react';
import { withRouter } from 'react-router-dom';
import { 
  Card, 
  CardMedia, 
  CardContent, 
  Typography, 
  Box, 
  Chip,
  IconButton
} from '@mui/material';
import { PlayArrow, Visibility } from '@mui/icons-material';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import './VideoCard.css';

const VideoCard = ({ video, onClick, showStats = true, history }) => {

  const handleClick = () => {
    if (onClick) {
      onClick(video);
    } else {
      history.push(`/video/${video._id}`);
    }
  };

  const formatViewCount = (count) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count?.toString() || '0';
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Card 
      className="video-card"
      onClick={handleClick}
      sx={{ 
        cursor: 'pointer',
        backgroundColor: '#1a1a1a',
        color: '#fff',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 16px rgba(0,0,0,0.3)'
        }
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <CardMedia
          component="img"
          image={video.thumbnailUrl || '/placeholder-video.jpg'}
          alt={video.title}
          sx={{ 
            objectFit: 'cover',
            width: '100%',
            height: 'auto',
            maxHeight: '240px',
            minHeight: '120px',
            aspectRatio: 'auto'
          }}
        />
        
        {/* Play button overlay */}
        <Box
          className="play-overlay"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.3)',
            opacity: 0,
            transition: 'opacity 0.2s',
            '&:hover': {
              opacity: 1
            }
          }}
        >
          <IconButton
            sx={{
              backgroundColor: 'rgba(255,255,255,0.9)',
              '&:hover': {
                backgroundColor: '#fff'
              }
            }}
          >
            <PlayArrow sx={{ fontSize: 40, color: '#000' }} />
          </IconButton>
        </Box>

        {/* Duration chip */}
        {video.duration && (
          <Chip
            label={formatDuration(video.duration)}
            size="small"
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              backgroundColor: 'rgba(0,0,0,0.8)',
              color: '#fff',
              fontSize: '12px'
            }}
          />
        )}
      </Box>

      <CardContent sx={{ p: 2 }}>
        <Typography 
          variant="subtitle1" 
          component="h3"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            fontWeight: 500,
            mb: 1
          }}
        >
          {video.title}
        </Typography>

        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ mb: 1 }}
        >
          {video.uploader?.fullName || 'Unknown Artist'}
        </Typography>

        {showStats && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Visibility sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {formatViewCount(video.views)} views
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {formatDistanceToNow(new Date(video.createdAt), { addSuffix: true })}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default withRouter(VideoCard);